import apiService from './apiService';
import { GET, POST, PUT, PATCH, DELETE } from './executer';

/**
 * Team entity from the database
 */
export interface Team {
  team_id: string;
  display_name: string;
  tio_account_id: string;
  api_key: string;
  admin_emails: string[];
  updated_by?: string | null;
  update_datetime?: string | null;
  deleted?: boolean;
}

/**
 * Request payload for creating a new team
 */
export interface CreateTeamRequest {
  team_id: string;
  display_name: string;
  tio_account_id: string;
  api_key: string;
  admin_emails?: string[];
}

/**
 * Request payload for updating a team
 */
export interface UpdateTeamRequest {
  team_id?: string;
  display_name?: string;
  tio_account_id?: string;
  api_key?: string;
  admin_emails?: string[];
}

/**
 * Response from the teams API
 */
export interface TeamsResponse {
  success: boolean;
  data: Team[];
  count: number;
}

export interface TeamResponse {
  success: boolean;
  data: Team;
}

/**
 * Usage report entry
 */
export interface UsageReportEntry {
  tio_account_id: string;
  display_name: string;
  sessions_count: number;
  users_count: number;
}

/**
 * Usage report response
 */
export interface UsageReportResponse {
  success: boolean;
  data: UsageReportEntry[];
  count: number;
  date_range: {
    start_date: string;
    end_date: string;
  };
}

class TeamsService {
  /**
   * Get all teams
   * @param includeDeleted - If true, includes soft-deleted teams (for management UI)
   */
  async getAllTeams(includeDeleted: boolean = false): Promise<Team[]> {
    try {
      const url = includeDeleted ? '/api/admin/teams?include_deleted=true' : '/api/admin/teams';
      const response = await apiService.client.execute<Team[]>(
        GET,
        url
      );
      
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error('Failed to fetch teams');
    } catch (error) {
      console.error('Error fetching teams:', error);
      throw error;
    }
  }

  /**
   * Get a team by ID
   */
  async getTeamById(teamId: string): Promise<Team> {
    try {
      const response = await apiService.client.execute<Team>(
        GET,
        `/api/admin/teams/${teamId}`
      );
      
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error('Failed to fetch team');
    } catch (error) {
      console.error(`Error fetching team ${teamId}:`, error);
      throw error;
    }
  }

  /**
   * Create a new team
   * Requires: root role OR admin API key
   */
  async createTeam(teamData: CreateTeamRequest, userToken: string) {
    return await apiService.client.execute<Team>(
      POST,
      '/api/admin/teams',
      {
        data: teamData,
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
  }

  /**
   * Update an existing team
   * Requires: root role OR admin API key
   */
  async updateTeam(
    teamId: string,
    updates: UpdateTeamRequest,
    userToken: string
  ) {
    return await apiService.client.execute<Team>(
      PUT,
      `/api/admin/teams/${teamId}`,
      {
        data: updates,
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
  }

  /**
   * Soft-delete a team
   * Requires: root role OR admin API key
   */
  async deleteTeam(teamId: string, userToken: string) {
    return await apiService.client.execute<{ message: string }>(
      DELETE,
      `/api/admin/teams/${teamId}`,
      {
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
  }

  /**
   * Set the deleted flag on a team (soft-delete or restore)
   * Requires: root role OR admin API key
   */
  async setTeamDeleted(teamId: string, deleted: boolean, userToken: string) {
    return await apiService.client.execute<{ success: boolean; message: string }>(
      PATCH,
      `/api/admin/teams/${teamId}/deleted`,
      {
        data: { deleted },
        headers: {
          Authorization: `Bearer ${userToken}`,
        },
      }
    );
  }

  /**
   * Get usage report with sessions and users count per team
   * Requires: root role OR admin API key
   * @param startDate - Start date in ISO timestamp format (e.g., "2025-01-01T00:00:00Z")
   * @param endDate - End date in ISO timestamp format (e.g., "2025-01-08T00:00:00Z")
   * @param userToken - User authentication token
   */
  async getUsageReport(
    startDate: string,
    endDate: string,
    userToken: string
  ): Promise<UsageReportResponse> {
    try {
      const response = await apiService.client.execute<UsageReportEntry[]>(
        GET,
        `/api/admin/usage-report?start_date=${encodeURIComponent(startDate)}&end_date=${encodeURIComponent(endDate)}`,
        {
          headers: {
            Authorization: `Bearer ${userToken}`,
          },
        }
      );
      
      if (response.success && response.raw) {
        // The executer extracts data, but we need the full response with count and date_range
        return response.raw as UsageReportResponse;
      }
      throw new Error('Failed to fetch usage report');
    } catch (error) {
      console.error('Error fetching usage report:', error);
      throw error;
    }
  }
}

export const teamsService = new TeamsService();
export default teamsService;
